// Public landing page for the one-click unsubscribe link in our status /
// transition emails. Reads `itemId` + `unsubscribeKey` from the query string
// and POSTs them to the TaskPogoUnsubscribe Lambda Function URL.
//
// The link in the email points at `https://www.taskpogo.com/unsubscribe?...`
// (see Urls.urlForUnsubscribe) so the user always lands on a TaskPogo page
// even when JavaScript is disabled or the email client follows the link in
// a stripped-down browser. The Function URL itself also responds 200 to a
// direct GET (the SES Lambda renders an HTML confirmation), so this page is
// the *enhanced* path -- it lets the user see a TaskPogo-branded result
// while still confirming the unsubscribe over the network.

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import outputs from '../amplify_outputs.json';

// The unsubscribe Lambda's public Function URL is published into
// amplify_outputs.json by the Gen 2 backend
// (`backend.addOutput({ custom: { unsubscribeFunctionUrl } })` in
// amplify/backend.ts) and baked into the bundle when the build copies that file
// into src/. Each branch (dev/prod) thus gets its own URL with no
// environment-variable wiring. Read at call time (rather than destructured at
// module load) so tests can swap the value between cases.
function getUnsubscribeEndpoint() {
  return outputs?.custom?.unsubscribeFunctionUrl || '';
}

const Status = {
  Loading: 'loading',
  Success: 'success',
  Error: 'error',
  Missing: 'missing',
};

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const itemId = searchParams.get('itemId');
  const unsubscribeKey = searchParams.get('unsubscribeKey');

  const [status, setStatus] = useState(
    itemId && unsubscribeKey ? Status.Loading : Status.Missing
  );
  const [message, setMessage] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!itemId || !unsubscribeKey) return;
      const endpoint = getUnsubscribeEndpoint();
      if (!endpoint) {
        setStatus(Status.Error);
        setMessage('Unsubscribe endpoint is not configured for this environment.');
        return;
      }
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemId, unsubscribeKey }),
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok && data.ok !== false) {
          setStatus(Status.Success);
          setMessage(data.message || 'You have been unsubscribed.');
        } else {
          setStatus(Status.Error);
          setMessage(data.error || 'We could not process your request. Please try again later.');
        }
      } catch (err) {
        if (cancelled) return;
        setStatus(Status.Error);
        setMessage('We could not reach the unsubscribe service. Please try again later.');
      }
    }
    run();
    return () => { cancelled = true; };
  }, [itemId, unsubscribeKey]);

  return (
    <div className="container py-5" style={{ maxWidth: 640 }}>
      <h1 className="h3 mb-3">Email subscriptions</h1>
      {status === Status.Missing && (
        <p className="text-body-secondary">
          This unsubscribe link is missing required information. Please use the link
          from the most recent email you received from TaskPogo, or sign in to manage
          your subscriptions directly.
        </p>
      )}
      {status === Status.Loading && (
        <p className="text-body-secondary">Processing your request&hellip;</p>
      )}
      {status === Status.Success && (
        <>
          <p>{message}</p>
          <p>
            <a href="/">Return to TaskPogo</a>
          </p>
        </>
      )}
      {status === Status.Error && (
        <>
          <p className="text-danger">{message}</p>
          <p>
            If the problem continues, please <a href="/support">contact support</a>.
          </p>
        </>
      )}
    </div>
  );
};

export default Unsubscribe;
